import { useEffect, useState } from 'react';
import { RGB, PaletteData } from '../types';
import { getColorFromImageData, rgbToHex, adjustColor } from '../utils/colorUtils';

export const usePalette = (imageUrl: string | null) => {
  const [data, setData] = useState<PaletteData>({
    vibrant: null,
    darkVibrant: null,
    darkMuted: null,
    loading: true
  });

  useEffect(() => {
    if (!imageUrl) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    const extractColors = async () => {
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            throw new Error('Could not get canvas context');
          }

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const centerColor = getColorFromImageData(
            imageData,
            Math.floor(canvas.width * 0.25),
            Math.floor(canvas.height * 0.25),
            Math.floor(canvas.width * 0.5),
            Math.floor(canvas.height * 0.5)
          );

          const darkVibrantColor = adjustColor(centerColor, 0.7);
          const darkMutedColor = adjustColor(centerColor, 0.5);

          setData({
            vibrant: rgbToHex(centerColor.r, centerColor.g, centerColor.b),
            darkVibrant: rgbToHex(
              darkVibrantColor.r,
              darkVibrantColor.g,
              darkVibrantColor.b
            ),
            darkMuted: rgbToHex(
              darkMutedColor.r,
              darkMutedColor.g,
              darkMutedColor.b
            ),
            loading: false
          });
        };

        img.onerror = () => {
          setData(prev => ({
            ...prev,
            loading: false
          }));
        };

        img.src = imageUrl;
      } catch (error) {
        setData(prev => ({
          ...prev,
          loading: false
        }));
      }
    };

    extractColors();
  }, [imageUrl]);

  return { data };
};
